export type DetectedOrientation =
  | 'EMPTY'
  | 'LANDSCAPE'
  | 'PORTRAIT'
  | 'SQUARE'
  | 'UNKNOWN';

export function normalizeDetectedOrientation(
  raw?: string | null,
): DetectedOrientation {
  if (!raw || raw.trim() === '') return 'EMPTY';
  const upper = raw.trim().toUpperCase();
  if (upper === 'LANDSCAPE' || upper === 'PORTRAIT' || upper === 'SQUARE') {
    return upper;
  }
  if (upper === 'ERROR' || upper === 'UNKNOWN') return 'UNKNOWN';
  return 'UNKNOWN';
}

export function orientationsCompatible(
  creative: DetectedOrientation,
  screenOrientation?: string,
): 'match' | 'mismatch' | 'unknown' | 'neutral' {
  if (creative === 'EMPTY') return 'neutral';
  if (creative === 'UNKNOWN') return 'unknown';
  const screen = String(screenOrientation || '').toUpperCase();
  if (!screen) return 'neutral';
  if (creative === 'SQUARE') {
    return screen === 'SQUARE' ? 'match' : 'mismatch';
  }
  if (screen === 'CUSTOM') return 'neutral';
  return creative === screen ? 'match' : 'mismatch';
}

export type CompatibilityCopy = {
  tone: 'info' | 'success' | 'warn' | 'danger';
  title: string;
  body: string;
};

export function buildCompatibilityCopy(opts: {
  detecting?: boolean;
  creative: DetectedOrientation;
  screenOrientation?: string;
  selectionSource?: 'auto' | 'manual' | null;
}): CompatibilityCopy | null {
  if (opts.detecting) {
    return {
      tone: 'info',
      title: 'Analyzing your creative orientation…',
      body: 'Screen recommendations pause until detection finishes.',
    };
  }

  // When no creative has been uploaded, do not display any warning
  if (opts.creative === 'EMPTY') {
    return null;
  }

  const status = orientationsCompatible(
    opts.creative,
    opts.screenOrientation,
  );

  if (opts.creative === 'UNKNOWN') {
    return {
      tone: 'warn',
      title: "We couldn't determine your creative's orientation",
      body: 'Please verify the media or select a supported screen configuration manually.',
    };
  }

  if (opts.creative === 'SQUARE' && status !== 'match') {
    return {
      tone: 'warn',
      title: 'We detected a nonstandard aspect ratio',
      body: 'Review the compatible screen options before continuing. Square creatives are not auto-classified as landscape or portrait.',
    };
  }

  if (status === 'mismatch') {
    const creativeLabel = opts.creative.toLowerCase();
    const screenLabel = String(opts.screenOrientation || '').toLowerCase();
    return {
      tone: 'warn',
      title: `Creative is ${creativeLabel}; selected screen is ${screenLabel}`,
      body: 'The player will stretch and crop this creative to fill the screen. Matching orientation looks best — check Live Preview device tabs, or add an alternate creative at review.',
    };
  }

  if (opts.creative === 'LANDSCAPE' && status === 'match') {
    return {
      tone: 'success',
      title: 'Landscape creative detected',
      body: 'You can select landscape, portrait, or mixed screens for this campaign.',
    };
  }

  if (opts.creative === 'PORTRAIT') {
    if (status === 'match') {
      return {
        tone: 'success',
        title: 'Portrait creative detected',
        body: 'Portrait screen selected. Switch Live Preview tabs to compare landscape and portrait playback. Other orientations remain available.',
      };
    }
    return {
      tone: 'info',
      title: 'Portrait creative detected',
      body: 'Select any screen types you need. Use Landscape / Portrait tabs to preview how the creative fills each display.',
    };
  }

  if (opts.creative === 'LANDSCAPE' && !opts.screenOrientation) {
    return {
      tone: 'info',
      title: 'Landscape creative detected',
      body: 'All eligible screen types are available. Matching landscape screens are marked when they align with your creative.',
    };
  }

  return null;
}

/** Prefer screens matching creative orientation when sorting catalog results. */
export function orientationRank(
  creative: DetectedOrientation,
  screenOrientation?: string,
) {
  if (creative === 'EMPTY' || creative === 'UNKNOWN') return 1;
  const screen = String(screenOrientation || '').toUpperCase();
  if (!screen) return 1;
  if (creative === 'SQUARE') return screen === 'SQUARE' ? 0 : 2;
  return screen === creative ? 0 : 2;
}

export type PreviewDeviceOrientation = 'LANDSCAPE' | 'PORTRAIT';

export function deviceKindOrientation(
  device: 'landscape' | 'portrait',
): PreviewDeviceOrientation {
  return device === 'landscape' ? 'LANDSCAPE' : 'PORTRAIT';
}

/**
 * How the player fills the screen when creative orientation ≠ device.
 * Match → contain (native). Mismatch → cover (stretch/crop to fill), matching
 * AWS creative adaptation for cross-orientation playback.
 */
export function resolvePreviewObjectFit(
  creative: DetectedOrientation,
  device: 'landscape' | 'portrait',
): 'contain' | 'cover' {
  if (creative === 'EMPTY' || creative === 'UNKNOWN') return 'contain';
  const deviceOrient = deviceKindOrientation(device);
  if (creative === 'SQUARE') return 'cover';
  return creative === deviceOrient ? 'contain' : 'cover';
}

export type DeviceFitAdvice = {
  tone: 'success' | 'warn' | 'info';
  title: string;
  body: string;
  suggestion: string;
};

export function buildDeviceFitAdvice(opts: {
  creative: DetectedOrientation;
  device: 'landscape' | 'portrait';
  detecting?: boolean;
}): DeviceFitAdvice | null {
  if (opts.detecting) {
    return {
      tone: 'info',
      title: 'Detecting creative orientation…',
      body: 'We analyze your media so the live preview matches how Screenista players adapt it.',
      suggestion: 'Wait a moment — preview will update automatically.',
    };
  }

  if (opts.creative === 'EMPTY') return null;

  const deviceOrient = deviceKindOrientation(opts.device);
  const deviceLabel =
    opts.device === 'portrait' ? 'portrait display' : 'landscape display';

  if (opts.creative === 'UNKNOWN') {
    return {
      tone: 'warn',
      title: 'Orientation could not be confirmed',
      body: 'Preview may not match on-device playback until orientation is known.',
      suggestion:
        'Re-upload the creative or switch device tabs to check landscape and portrait looks.',
    };
  }

  if (opts.creative === 'SQUARE') {
    return {
      tone: 'warn',
      title: 'Square creative on a fixed display',
      body: `On a ${deviceLabel} the player fills the panel, which can crop edges.`,
      suggestion:
        'Prefer a landscape or portrait creative that matches the screens you book.',
    };
  }

  if (opts.creative === deviceOrient) {
    return {
      tone: 'success',
      title: `Native fit on ${deviceLabel}`,
      body: 'Your creative matches this device orientation — playback keeps the full frame without stretch.',
      suggestion:
        'Use the tabs above to check how the same creative would look on the other orientation before booking.',
    };
  }

  const creativeLabel = opts.creative.toLowerCase();
  return {
    tone: 'warn',
    title: `${opts.creative} creative → ${deviceLabel}`,
    body: `Playback will stretch and crop your ${creativeLabel} creative to fill this ${deviceLabel} (same adaptation used when orientation does not match the selected screen).`,
    suggestion:
      opts.creative === 'PORTRAIT'
        ? 'For landscape screens, upload a landscape creative (or add one at review). Keep portrait screens for this file.'
        : 'For portrait screens, upload a matching portrait creative (or add one at review). Prefer landscape screens for this file.',
  };
}
